import { LegalPageLayout } from '../components';

export function TermsOfServicePage() {
  return (
    <LegalPageLayout title="Terms of Service" lastUpdated="March 13, 2026">
      <p>
        Welcome to Fias. These Terms of Service (&quot;Terms&quot;) govern your access to and use of
        the Fias platform, website, and services (collectively, the &quot;Service&quot;) operated by
        Fias Inc., a Nevada corporation (&quot;Fias,&quot; &quot;we,&quot; &quot;us,&quot; or
        &quot;our&quot;). By creating an account or using the Service, you agree to be bound by
        these Terms, our <a href="/legal/privacy">Privacy Policy</a>, and our{' '}
        <a href="/legal/acceptable-use">Acceptable Use Policy</a>.
      </p>
      <p>
        If you are using the Service on behalf of an organization, you represent that you have
        authority to bind that organization to these Terms.
      </p>
      <p>
        <strong>
          Please read these Terms carefully, including the binding arbitration clause and class
          action waiver in Section 14.
        </strong>
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You must be at least 18 years old to use the Service. By creating an account, you represent
        and warrant that you meet this age requirement. We do not knowingly collect information from
        anyone under 18.
      </p>
      <p>
        You may not use the Service if you are located in a country subject to U.S. government
        embargo or designated as a &quot;terrorist supporting&quot; country, or if you are listed on
        any U.S. government list of prohibited or restricted parties.
      </p>

      <h2>2. Accounts</h2>
      <p>
        To use most features of the Service, you must create an account. You agree to provide
        accurate, current, and complete information during registration and to keep your account
        information up to date. You are responsible for safeguarding your password and for all
        activity that occurs under your account.
      </p>
      <p>
        Organizations may create accounts. The individual who creates the account represents that
        they are authorized to act on behalf of the organization and to bind the organization to
        these Terms.
      </p>
      <p>
        You must notify us immediately of any unauthorized use of your account. We are not liable
        for any loss arising from unauthorized use of your account.
      </p>

      <h2>3. The Service</h2>
      <p>
        Fias is an AI orchestration platform where users can discover, use, and create AI-powered
        applications called &quot;Arches.&quot; The Service includes:
      </p>
      <ul>
        <li>
          <strong>AI-powered applications (Arches)</strong> — interactive applications that
          orchestrate AI models and other services to accomplish tasks
        </li>
        <li>
          <strong>Marketplace</strong> — a directory where users can discover and use Arches created
          by contributors
        </li>
        <li>
          <strong>Plugin Builder</strong> — tools for creating and publishing custom Arches
        </li>
        <li>
          <strong>Chat and community features</strong> — channels, direct messages, and guilds
        </li>
        <li>
          <strong>Virtual items and game features</strong> — inventory, crafting, and reward systems
        </li>
      </ul>
      <p>
        We reserve the right to modify, suspend, or discontinue any part of the Service at any time,
        with or without notice. We will make reasonable efforts to provide advance notice of
        material changes.
      </p>

      <h2>4. AI-Powered Features</h2>
      <p>
        The Service uses third-party artificial intelligence models, including those provided by
        OpenAI, Anthropic, and Google, to generate content and power Arches. By using AI-powered
        features, you acknowledge and agree that:
      </p>
      <ul>
        <li>
          <strong>Your inputs are processed by third-party AI providers.</strong> When you use
          AI-powered features, the text and data you provide may be sent to third-party AI service
          providers for processing. These providers have their own terms of service and privacy
          policies.
        </li>
        <li>
          <strong>AI outputs may be inaccurate.</strong> AI-generated content is provided &quot;as
          is&quot; and may contain errors, inaccuracies, or inappropriate content. You are
          responsible for reviewing and verifying any AI-generated output before relying on it.
        </li>
        <li>
          <strong>AI outputs are not professional advice.</strong> Nothing generated by the Service
          constitutes legal, medical, financial, or other professional advice. You should consult
          qualified professionals for such matters.
        </li>
        <li>
          <strong>We do not use your content to train AI models.</strong> Your conversations,
          inputs, and content are not used by Fias to train or improve AI models. Third-party AI
          providers may have their own data usage policies, which we encourage you to review.
        </li>
      </ul>

      <h2>5. Credits, Payments, and Billing</h2>

      <h3>5.1 Credits</h3>
      <p>
        Certain features of the Service require credits. Credits are a virtual unit of exchange used
        within the Service (1 credit = $0.01 USD) and have no monetary value outside the platform.
        Credits are consumed when you use AI-powered features, image generation, social media
        posting, and other cost-generating activities.
      </p>

      <h3>5.2 Purchasing Credits</h3>
      <p>
        You may purchase credits using a credit/debit card (processed by Stripe) or cryptocurrency
        (processed by Coinbase Commerce). Minimum purchase is $1.00 USD. All purchases are final and
        processed in US Dollars.
      </p>

      <h3>5.3 System Credits</h3>
      <p>
        New users receive a one-time welcome grant of system credits. Active free-tier users may
        receive periodic system credit replenishments at our discretion. System credits:
      </p>
      <ul>
        <li>Are not redeemable for cash or any other form of payment.</li>
        <li>Do not roll over beyond the maximum system credit balance.</li>
        <li>
          Are granted at Fias&apos;s sole discretion and may be modified or discontinued at any
          time.
        </li>
        <li>Are consumed before purchased credits when using platform features.</li>
      </ul>

      <h3>5.4 Subscriptions</h3>
      <p>
        We offer optional subscription plans that provide monthly credit allocations and enhanced
        features. Subscriptions are billed monthly through Stripe and automatically renew until
        canceled.
      </p>
      <ul>
        <li>
          <strong>Subscription credits reset monthly and do not carry over.</strong> Each billing
          period, your subscription credit balance is replaced with the plan&apos;s monthly
          allocation. Any unused subscription credits from the previous period are forfeited.
          Purchased Fias credits are separate and continue to roll over.
        </li>
        <li>
          You may cancel your subscription at any time. Cancellation takes effect at the end of the
          current billing period.
        </li>
        <li>No prorated refunds are provided for partial billing periods.</li>
        <li>
          We will provide at least 30 days&apos; advance notice of any subscription price changes.
          Continued use after a price change constitutes acceptance.
        </li>
        <li>If payment fails, your subscription may be downgraded to the free tier.</li>
      </ul>

      <h3>5.5 Refund Policy</h3>
      <p>Credits are generally non-refundable, except in cases of:</p>
      <ul>
        <li>Platform error or service outage that prevented use of purchased credits.</li>
        <li>Unauthorized purchases (reported within 30 days).</li>
        <li>Duplicate charges.</li>
        <li>Where required by applicable law.</li>
      </ul>
      <p>
        Fias administrators may issue discretionary refunds on a case-by-case basis. Refunds for
        credit card purchases are processed through Stripe. Cryptocurrency purchases are final and
        non-refundable due to the nature of blockchain transactions.
      </p>

      <h3>5.6 Creator Payouts</h3>
      <p>
        Creators who publish paid Arches may earn revenue from user executions. Creator earnings are
        subject to the following terms:
      </p>
      <ul>
        <li>A platform fee (currently 15%) is deducted from each transaction.</li>
        <li>
          Earnings are held for a 7-day dispute resolution period before becoming available for
          payout.
        </li>
        <li>Minimum payout threshold is $10.00 USD.</li>
        <li>
          Payouts are processed via Stripe Connect and require completion of identity verification
          (KYC).
        </li>
        <li>An active paid subscription (Standard or Pro) is required to receive payouts.</li>
        <li>
          You are responsible for all applicable taxes on your earnings. Fias will issue tax forms
          (e.g., 1099) as required by law.
        </li>
      </ul>

      <h3>5.7 Cryptocurrency Payments</h3>
      <p>
        Payments made via cryptocurrency (Coinbase Commerce) are final and non-refundable once
        confirmed on the blockchain. We are not responsible for:
      </p>
      <ul>
        <li>Network confirmation delays or failures.</li>
        <li>Payments sent to incorrect addresses.</li>
        <li>Cryptocurrency value fluctuations between payment initiation and confirmation.</li>
      </ul>

      <h3>5.8 Account Termination and Credits</h3>
      <p>
        If your account is terminated for violation of these Terms, any remaining credit balance may
        be forfeited. If you voluntarily close your account, unused purchased credits will be
        forfeited 30 days after account closure. Creator earnings that have cleared the hold period
        will be paid out upon request before account closure.
      </p>

      <h3>5.9 Price Changes</h3>
      <p>
        We reserve the right to modify credit pricing, subscription rates, earning rates, platform
        fees, and usage costs at any time. We will provide at least 30 days&apos; advance notice of
        material changes via email or in-platform notification.
      </p>

      <h2>6. User Content</h2>
      <p>
        &quot;User Content&quot; means any content you create, upload, submit, or transmit through
        the Service, including messages, conversation data, Arches, plugins, and other materials.
      </p>
      <p>
        <strong>You retain ownership of your User Content.</strong> By submitting User Content to
        the Service, you grant Fias a worldwide, non-exclusive, royalty-free license to use,
        reproduce, modify, display, and distribute your User Content solely as necessary to operate,
        maintain, and provide the Service. This license continues for the duration your content
        remains on the platform.
      </p>
      <p>
        You represent and warrant that you own or have the necessary rights to your User Content and
        that your User Content does not infringe, misappropriate, or violate any third party&apos;s
        intellectual property or other rights.
      </p>

      <h2>7. AI-Generated Output Ownership</h2>
      <p>
        Subject to the terms of the underlying AI providers, you retain rights to the output
        generated by AI models through your use of the Service. However, AI-generated outputs may
        not be unique to you — other users may receive similar or identical outputs. Fias does not
        claim ownership of AI-generated outputs but retains the right to display them within the
        Service as part of normal platform operation.
      </p>

      <h2>8. Marketplace and Contributor Terms</h2>
      <p>
        Contributors who create and publish Arches to the marketplace grant Fias a non-exclusive,
        worldwide, royalty-free license to display, distribute, and make available their Arches
        through the Service. Contributors retain ownership of their Arches.
      </p>
      <ul>
        <li>
          All Arches are subject to review before being published to the marketplace. Fias reserves
          the right to reject, remove, or suspend any Arche at its sole discretion.
        </li>
        <li>
          Fias acts as a marketplace operator, not a publisher. We do not endorse, guarantee, or
          assume liability for any third-party Arches available through the marketplace.
        </li>
        <li>
          Revenue sharing and contributor-specific terms are governed by a separate Contributor
          Agreement, which contributors must accept before publishing Arches.
        </li>
        <li>
          If you delete your account, your published Arches will also be deleted from the
          marketplace.
        </li>
      </ul>

      <h2>9. Virtual Items and Game Features</h2>
      <p>
        The Service may include virtual items, inventory systems, crafting, rewards, and
        subscription-based game features. You acknowledge that:
      </p>
      <ul>
        <li>
          Virtual items have no real-world monetary value and cannot be sold, traded, or transferred
          outside the platform.
        </li>
        <li>
          We reserve the right to modify, rebalance, or remove virtual items and game features at
          any time.
        </li>
        <li>
          Virtual items and game progress may be lost if the Service is discontinued or your account
          is terminated.
        </li>
        <li>
          Game subscriptions, if applicable, will auto-renew unless cancelled before the renewal
          date.
        </li>
      </ul>

      <h2>10. Acceptable Use</h2>
      <p>
        Your use of the Service is subject to our{' '}
        <a href="/legal/acceptable-use">Acceptable Use Policy</a>, which is incorporated into these
        Terms by reference. Violation of the Acceptable Use Policy may result in suspension or
        termination of your account.
      </p>

      <h2>11. Account Termination</h2>
      <p>
        <strong>By you:</strong> You may close your account at any time through your account
        settings or by contacting us. Upon closure, your data will be handled in accordance with our
        Privacy Policy.
      </p>
      <p>
        <strong>By us:</strong> We may suspend or terminate your account at our discretion if we
        reasonably believe you have violated these Terms or our Acceptable Use Policy. Where
        practicable, we will provide notice and an opportunity to cure before termination, except in
        cases of severe or repeated violations.
      </p>
      <p>
        Upon termination, your right to use the Service ceases immediately. Sections 6, 7, 12, 13,
        14, and 15 survive termination.
      </p>

      <h2>12. Disclaimers</h2>
      <p>
        <strong>
          THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES
          OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
        </strong>
      </p>
      <p>
        We do not warrant that the Service will be uninterrupted, secure, or error-free, that any
        content or AI-generated output will be accurate or reliable, or that any defects will be
        corrected.
      </p>
      <p>
        You use the Service at your own risk. You are solely responsible for any damage to your
        computer system or loss of data resulting from your use of the Service.
      </p>

      <h2>13. Limitation of Liability</h2>
      <p>
        <strong>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, FIAS AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND
          AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
          PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR
          RELATED TO YOUR USE OF THE SERVICE, REGARDLESS OF THE THEORY OF LIABILITY.
        </strong>
      </p>
      <p>
        <strong>
          OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR
          THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID TO FIAS IN THE TWELVE
          (12) MONTHS PRECEDING THE CLAIM OR (B) ONE HUNDRED DOLLARS ($100).
        </strong>
      </p>

      <h2>14. Dispute Resolution and Arbitration</h2>
      <p>
        <strong>Please read this section carefully. It affects your legal rights.</strong>
      </p>
      <h3>Informal Resolution</h3>
      <p>
        Before filing any formal dispute, you agree to contact us at{' '}
        <a href="mailto:legal@fias.io">legal@fias.io</a> and attempt to resolve the dispute
        informally for at least 30 days.
      </p>
      <h3>Binding Arbitration</h3>
      <p>
        If we cannot resolve a dispute informally, you and Fias agree to resolve any dispute, claim,
        or controversy arising out of or relating to these Terms or the Service through binding
        arbitration administered by the American Arbitration Association (&quot;AAA&quot;) under its
        Consumer Arbitration Rules. Arbitration shall take place in Albuquerque, New Mexico, or may
        be conducted virtually at the election of either party.
      </p>
      <h3>Class Action Waiver</h3>
      <p>
        <strong>
          YOU AND FIAS AGREE THAT DISPUTES WILL BE RESOLVED ON AN INDIVIDUAL BASIS AND THAT NEITHER
          PARTY SHALL BRING OR PARTICIPATE IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.
        </strong>
      </p>
      <h3>Opt-Out</h3>
      <p>
        You may opt out of this arbitration provision by sending written notice to{' '}
        <a href="mailto:legal@fias.io">legal@fias.io</a> within 30 days of creating your account. If
        you opt out, disputes will be resolved in the state or federal courts located in Bernalillo
        County, New Mexico.
      </p>
      <h3>Small Claims Exception</h3>
      <p>
        Either party may bring an individual action in small claims court for disputes within the
        court&apos;s jurisdiction.
      </p>

      <h2>15. General Provisions</h2>
      <h3>Governing Law</h3>
      <p>
        These Terms are governed by the laws of the State of New Mexico, without regard to conflict
        of law principles.
      </p>
      <h3>Changes to Terms</h3>
      <p>
        We may update these Terms from time to time. If we make material changes, we will notify you
        by posting the updated Terms on the Service with a new &quot;Last Updated&quot; date. Your
        continued use of the Service after the changes take effect constitutes your acceptance of
        the revised Terms.
      </p>
      <h3>Severability</h3>
      <p>
        If any provision of these Terms is found to be unenforceable, the remaining provisions will
        continue in full force and effect. The unenforceable provision will be modified to the
        minimum extent necessary to make it enforceable.
      </p>
      <h3>Entire Agreement</h3>
      <p>
        These Terms, together with the Privacy Policy, Acceptable Use Policy, and any applicable
        Contributor Agreement, constitute the entire agreement between you and Fias regarding the
        Service.
      </p>
      <h3>Waiver</h3>
      <p>
        Our failure to enforce any provision of these Terms shall not constitute a waiver of that
        provision or any other provision.
      </p>
      <h3>Assignment</h3>
      <p>
        You may not assign or transfer your rights under these Terms without our prior written
        consent. We may assign our rights and obligations without restriction.
      </p>

      <h2>16. Contact</h2>
      <p>If you have questions about these Terms, please contact us at:</p>
      <p>
        Fias Inc.
        <br />
        Email: <a href="mailto:legal@fias.io">legal@fias.io</a>
      </p>
    </LegalPageLayout>
  );
}
